import { common_lang_file } from './common';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function debounce(fn, timeoutMs) {
    let timerId;

    return (...args) => {
        window.clearTimeout(timerId);
        timerId = window.setTimeout(() => fn(...args), timeoutMs);
    };
}

function formatNumber(value) {
    const numeric = Number(value ?? 0);
    return Number.isFinite(numeric)
        ? new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 2 }).format(numeric)
        : '';
}

function formatDate(value) {
    if (!value) {
        return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
    }).format(parsed);
}

function appendQueryParam(url, key, value) {
    if (!url || !value) {
        return url || '#';
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${key}=${encodeURIComponent(value)}`;
}

function renderGroupItems(groupKey, items) {
    if (!items || items.length === 0) {
        return '';
    }

    if (groupKey === 'transactions') {
        return items.map(item => {
            const signedAmount = Number(item.transactionAmount ?? 0);
            const typeClass = (item.typeId ?? 0) < 0 ? 'text-danger' : (item.typeId ?? 0) > 0 ? 'text-success' : 'text-warning';
            const tags = item.tagsByCommas ? `, ${escapeHtml(item.tagsByCommas)}` : '';
            const subtitle = `${escapeHtml(item.accountFullName || '')} • ${formatDate(item.transactionDate)}${tags}`;

            return `
                <a class="dropdown-item topbar-search-item" href="${escapeHtml(item.url || '#')}">
                    <div class="d-flex align-items-center justify-content-between gap-2 topbar-search-item-row">
                        <div class="topbar-search-item-main">
                            <div class="topbar-search-item-text">
                                <div class="fw-medium text-truncate">${escapeHtml(item.description || item.accountFullName || '#')}</div>
                                <div class="small text-muted text-truncate">${subtitle}</div>
                            </div>
                        </div>
                        <div class="small fw-semibold ${typeClass}">${formatNumber(signedAmount)}</div>
                    </div>
                </a>`;
        }).join('');
    }

    if (groupKey === 'tags') {
        return items.map(item => `
            <a class="dropdown-item topbar-search-item" href="${escapeHtml(item.url || '#')}">
                <div class="d-flex align-items-center gap-2 min-w-0">
                    <div class="d-flex align-items-center gap-2 min-w-0">
                        <div class="text-truncate">
                            <div class="text-truncate fw-medium">#${escapeHtml(item.tagName)}</div>
                            <div class="small text-muted text-truncate">${formatNumber(item.transactionsAmount)}</div>
                        </div>
                    </div>
                </div>
            </a>`).join('');
    }

    if (groupKey === 'categories') {
        return items.map(item => `
            <a class="dropdown-item topbar-search-item" href="${escapeHtml(item.url || '#')}">
                <div class="d-flex align-items-center gap-2 topbar-search-item-row">
                    <div class="topbar-search-item-text text-truncate fw-medium">${escapeHtml(item.categoryName)}</div>
                </div>
            </a>`).join('');
    }

    if (groupKey === 'accounts') {
        return items.map(item => `
            <a class="dropdown-item topbar-search-item" href="${escapeHtml(item.url || '#')}">
                <div class="d-flex align-items-center justify-content-between gap-2">
                    <div class="d-flex align-items-center gap-2 min-w-0">
                        <div class="text-truncate">
                            <div class="text-truncate fw-medium">${escapeHtml(item.accountFullName)}</div>
                            <div class="small text-muted text-truncate">${formatNumber(item.balance)} ${escapeHtml(item.currencyCode || '')}</div>
                        </div>
                    </div>
                </div>
            </a>`).join('');
    }

    if (groupKey === 'periods') {
        return items.map(item => {
            const fromDate = formatDate(item.fromDate);
            const toDate = formatDate(item.toDate);
            return `
                <a class="dropdown-item topbar-search-item" href="${escapeHtml(item.url || '#')}">
                    <div class="d-flex align-items-center gap-2 min-w-0">
                        <div class="text-truncate">
                            <div class="fw-medium text-truncate">${escapeHtml(item.periodName)}</div>
                            <div class="small text-muted text-truncate">${fromDate} - ${toDate}</div>
                        </div>
                    </div>
                </a>`;
        }).join('');
    }

    return '';
}

export function initTopbarSearch() {
    const searchRoots = Array.from(document.querySelectorAll('.topbar-search'));

    if (searchRoots.length === 0) {
        return;
    }

    const closeAllDropdowns = () => {
        searchRoots.forEach(root => {
            const dropdown = root.querySelector('.topbar-search-dropdown');
            if (dropdown) {
                dropdown.classList.add('d-none');
            }
        });
    };

    searchRoots.forEach(root => {
        const commonLang = common_lang_file() || {};
        const input = root.querySelector('.topbar-search-input');
        const dropdown = root.querySelector('.topbar-search-dropdown');
        const groupsContainer = root.querySelector('.topbar-search-groups');
        const loadingState = root.querySelector('.topbar-search-state-loading');
        const emptyState = root.querySelector('.topbar-search-state-empty');
        const searchUrl = root.getAttribute('data-search-url') || '/api/topbar/search';

        if (!input || !dropdown || !groupsContainer || !loadingState || !emptyState) {
            return;
        }

        const labels = {
            transactions: root.getAttribute('data-label-transactions') || 'Transactions',
            tags: root.getAttribute('data-label-tags') || 'Tags',
            categories: root.getAttribute('data-label-categories') || 'Categories',
            accounts: root.getAttribute('data-label-accounts') || 'Accounts',
            periods: root.getAttribute('data-label-periods') || 'Periods',
            viewAll: root.getAttribute('data-label-view-all') || 'View all',
            noResults: root.getAttribute('data-label-no-results') || commonLang.noResultsText || 'No results found.',
            searching: root.getAttribute('data-label-searching') || commonLang.searchingText || 'Searching...'
        };

        const allUrls = {
            transactions: root.getAttribute('data-url-all-transactions') || '/Transaction',
            tags: root.getAttribute('data-url-all-tags') || '/TransactionTag',
            categories: root.getAttribute('data-url-all-categories') || '/TransactionCategory',
            accounts: root.getAttribute('data-url-all-accounts') || '/UserAccount',
            periods: root.getAttribute('data-url-all-periods') || '/UserPeriod'
        };

        let currentAbortController = null;
        let activeIndex = -1;
        let currentQuery = '';

        const updateKeyboardSelection = () => {
            const items = Array.from(groupsContainer.querySelectorAll('.topbar-search-item'));
            items.forEach((item, index) => {
                item.classList.toggle('active', index === activeIndex);
            });
        };

        const render = (response) => {
            const groupsOrder = ['transactions', 'tags', 'categories', 'accounts', 'periods'];
            const html = groupsOrder
                .map(groupKey => {
                    const items = Array.isArray(response[groupKey]) ? response[groupKey] : [];
                    if (items.length === 0) {
                        return '';
                    }

                    const itemsHtml = renderGroupItems(groupKey, items);
                    if (!itemsHtml) {
                        return '';
                    }

                    let footerUrl = allUrls[groupKey] || '#';
                    if (groupKey === 'transactions') {
                        footerUrl = appendQueryParam(footerUrl, 'periodid', '-1');
                    }
                    if (groupKey === 'transactions' || groupKey === 'tags' || groupKey === 'categories' || groupKey === 'accounts' || groupKey === 'periods') {
                        footerUrl = appendQueryParam(footerUrl, 'search', currentQuery);
                    }

                    return `
                        <div class="topbar-search-group">
                            <div class="topbar-search-group-title">${escapeHtml(labels[groupKey])}</div>
                            <div class="topbar-search-group-items">${itemsHtml}</div>
                            <div class="topbar-search-group-footer">
                                <a class="topbar-search-view-all" href="${escapeHtml(footerUrl)}">${escapeHtml(labels.viewAll)}</a>
                            </div>
                        </div>`;
                })
                .filter(Boolean)
                .join('');

            groupsContainer.innerHTML = html;
            activeIndex = -1;

            loadingState.classList.add('d-none');

            if (!html) {
                emptyState.textContent = labels.noResults;
                emptyState.classList.remove('d-none');
            } else {
                emptyState.classList.add('d-none');
            }

            dropdown.classList.remove('d-none');
        };

        const clearResults = () => {
            groupsContainer.innerHTML = '';
            loadingState.classList.add('d-none');
            emptyState.classList.add('d-none');
            dropdown.classList.add('d-none');
            activeIndex = -1;
        };

        const search = async (query) => {
            if (query.length < MIN_QUERY_LENGTH) {
                clearResults();
                return;
            }

            if (currentAbortController) {
                currentAbortController.abort();
            }

            currentAbortController = new AbortController();

            loadingState.textContent = labels.searching;
            loadingState.classList.remove('d-none');
            emptyState.classList.add('d-none');
            dropdown.classList.remove('d-none');

            try {
                const url = `${searchUrl}?query=${encodeURIComponent(query)}`;
                const response = await fetch(url, {
                    method: 'GET',
                    credentials: 'same-origin',
                    headers: {
                        'Accept': 'application/json'
                    },
                    signal: currentAbortController.signal
                });

                if (!response.ok) {
                    throw new Error(`Topbar search request failed: ${response.status}`);
                }

                const payload = await response.json();
                render(payload || {});
            } catch (error) {
                if (error && error.name === 'AbortError') {
                    return;
                }

                clearResults();
            }
        };

        const searchDebounced = debounce(search, DEBOUNCE_MS);

        input.addEventListener('input', (event) => {
            const nextQuery = String(event.target.value || '').trim();
            currentQuery = nextQuery;
            searchDebounced(nextQuery);
        });

        input.addEventListener('keydown', (event) => {
            const items = Array.from(groupsContainer.querySelectorAll('.topbar-search-item'));
            if (items.length === 0) {
                if (event.key === 'Escape') {
                    clearResults();
                    input.blur();
                }
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                activeIndex = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
                updateKeyboardSelection();
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                activeIndex = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
                updateKeyboardSelection();
                return;
            }

            if (event.key === 'Enter' && activeIndex >= 0) {
                event.preventDefault();
                items[activeIndex].click();
                return;
            }

            if (event.key === 'Escape') {
                clearResults();
                input.blur();
            }
        });

        groupsContainer.addEventListener('mouseover', (event) => {
            const hoveredItem = event.target.closest('.topbar-search-item');
            if (!hoveredItem) {
                return;
            }

            const items = Array.from(groupsContainer.querySelectorAll('.topbar-search-item'));
            activeIndex = items.indexOf(hoveredItem);
            updateKeyboardSelection();
        });

        input.addEventListener('focus', () => {
            if (groupsContainer.childElementCount > 0) {
                dropdown.classList.remove('d-none');
            }
        });

        document.addEventListener('click', (event) => {
            if (!root.contains(event.target)) {
                clearResults();
            }
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeAllDropdowns();
        }
    });
}
